/* eslint-disable no-console */
import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteLocalDb } from '../../utils/LocalDB';
import useMessageHook from '../../containers/CollaborationRevised/customHooks/useMessageHook';
import { resetMediaKitData, setLoggingOutForMedia } from '../../redux/MediakitRedux';
import { getDealData, getUserSession } from '../../redux/UserSessionSlice';
import { certifciteTab, MarkingTab } from '../../redux/NetworkRedux';
import { getSocketSessionId, redirectToCognitoSignOut } from '../../libs/HelperContent';
import useStoreEmpty from '../../libs/CustomHooks/useStoreEmpty';
import { LoginContext } from '../../App';
import { useNavigate } from 'react-router-dom';
import { CallContext } from '../../libs/Context';
import { PRESENCE_ENUMS } from '../../libs/enums/presenceEnums';
import { setAudioCall, setVideoCall, setCallInProgress } from '../../redux/CallsRedux';
import { setCallDetails, setEachCallDetailChanges } from '../../redux/PresenceRedux';
import { store } from '../../redux/store';
import { getUserCompanyPreference, saveUserCompanyPreference } from '../../utils/companyDataUtils';

// Constants moved outside component for better performance
const RINGING_STATES = [
	PRESENCE_ENUMS.initiated,
	PRESENCE_ENUMS.outGoing,
	PRESENCE_ENUMS.incoming
];

const useLogout = () =>
{
	const dispatch = useDispatch();
	const { handleLogoutForCollaboration } = useMessageHook();
	const myMediaKitUploadFile = useSelector((state:any) => state.mymediakit.uploadFile);
	const emptyReduxState = useStoreEmpty();
	const { setCatTab } = useContext(LoginContext);
	const { peerConnectionClose, updateCallDetails, setUpGroupCallInfo } =
		useContext(CallContext);
	const navigate = useNavigate();
	const session = useSelector((state:any) => state?.mysession?.session);
	const { socket } = useContext(LoginContext);

	/**
	 * Disconnects any active calls before logout
	 * - If call is in ringing state (initiated, outgoing, incoming) → decline it (like handleCallCancel)
	 * - If call is ongoing (connected) → end it with duration (like handleEndCall)
	 */
	const disconnectActiveCalls = async () =>
	{
		console.log('disconnectActiveCalls');
		try
		{
			// Get current call details from store
			const currentCallDetails:any = store?.getState()?.mypresence?.callDetails;
			const currentCallData = store?.getState()?.call;

			// Early return if no active call
			if (!currentCallDetails)
			{
				return;
			}

			const { audioCall, videoCall } = currentCallData || {};
			const callId = currentCallDetails?.callId;
			const callType = currentCallDetails?.type;
			const callStatus = currentCallDetails?.status;

			// Determine call state
			const isOngoing = callStatus === 'connected' ||
				audioCall === PRESENCE_ENUMS.onGoing ||
				videoCall === PRESENCE_ENUMS.onGoing;

			const isRinging = RINGING_STATES.includes(audioCall) ||
				RINGING_STATES.includes(videoCall);

			// Handle call disconnection based on state
			if (isOngoing && updateCallDetails)
			{
				// Calculate duration for ongoing calls
				const duration = currentCallDetails?.callStartTime
					? Math.floor((new Date().valueOf() - currentCallDetails.callStartTime) / 1000)
					: 0;

				await updateCallDetails(PRESENCE_ENUMS.ended, duration, currentCallDetails);
			}
			else if (isRinging)
			{
				// Decline ringing calls
				if (callType === PRESENCE_ENUMS.individual && updateCallDetails)
				{
					const status = currentCallDetails?.initiator === session?.userId
						? PRESENCE_ENUMS.missed
						: PRESENCE_ENUMS.declined;

					await updateCallDetails(status, null, currentCallDetails);
				}
				else if (callType === PRESENCE_ENUMS.group)
				{
					dispatch(setEachCallDetailChanges({
						status  : PRESENCE_ENUMS.declined,
						groupId : currentCallDetails?.groupId,
						callId
					}));

					if (setUpGroupCallInfo)
					{
						await setUpGroupCallInfo('declined', currentCallDetails);
					}
				}
			}

			// Clear call states (audio/video) - always clear regardless of call state
			if (audioCall === PRESENCE_ENUMS.onGoing || RINGING_STATES.includes(audioCall))
			{
				dispatch(setAudioCall(''));
			}

			if (videoCall === PRESENCE_ENUMS.onGoing || RINGING_STATES.includes(videoCall))
			{
				dispatch(setVideoCall(''));
			}

			// Close peer connection if available
			if (callId && peerConnectionClose)
			{
				peerConnectionClose(callId);
			}

			// Clear call details and call in progress
			dispatch(setCallDetails(null));
			dispatch(setCallInProgress(false));
		}
		catch (error)
		{
			console.error('Error disconnecting calls during logout:', error);
			// Continue with logout even if call disconnection fails
		}
	};

	const handleLogout = async () =>
	{
		const companyPreference = getUserCompanyPreference();

		try
		{
			// Collect all async operations in an array
			const logoutPromises: Promise<any>[] = [];

			// Step 1: Disconnect any active calls first
			logoutPromises.push(disconnectActiveCalls());

			// Step 2: Handle collaboration logout
			logoutPromises.push(handleLogoutForCollaboration());

			// Step 3: Clear all local storage
			logoutPromises.push(deleteLocalDb());

			await Promise.all(logoutPromises);

			// Step 4: Clear Redux state AFTER async operations
			// This prevents components from reacting to state changes
			emptyReduxState();

			// Step 5: Reset critical Redux slices to prevent API calls
			dispatch(getUserSession({}));
			dispatch(certifciteTab(0));
			dispatch(MarkingTab(0));
			dispatch(getDealData({ dealConnection: 0, dealStatus: false }));

			// Step 6: Handle media kit state
			myMediaKitUploadFile?.length > 0
				? dispatch(setLoggingOutForMedia(true))
				: dispatch(resetMediaKitData());

			// Step 7: Emit logout event to socket
			socket.emit('logout_from_current_device', {
				sessionId : getSocketSessionId(),
				userId    : session?.userId,
				masterId  : session?.masterId
			});

			// Step 8: Clear localStorage and sessionStorage
			localStorage.clear();
			sessionStorage.clear();

			// Step 9: Save company preference before clearing storage
			if (companyPreference?.masterId)
			{
				saveUserCompanyPreference(companyPreference?.masterId, companyPreference?.companyId);
			}

			// Step 10: Broadcast logout to other tabs
			const broadcastChannel = new BroadcastChannel('signOutChannel');

			broadcastChannel.postMessage('userSignedOut');
			broadcastChannel.close();

			// Step 11: Reset UI state
			setCatTab(0);

			// Step 12: Navigate to home page (after all cleanup is done)
			navigate('/');

			// Wait for all async operations to complete
			// await Promise.all(logoutPromises);

			// Step 13: Redirect to Cognito sign out (final step)
			await redirectToCognitoSignOut();
		}
		catch (error)
		{
			console.error('Error during logout:', error);
			// Even if there's an error, try to complete logout
			try
			{
				localStorage.clear();
				sessionStorage.clear();
				navigate('/');
				await redirectToCognitoSignOut();
			}
			catch (fallbackError)
			{
				console.error('Error in fallback logout:', fallbackError);
				// Force navigation as last resort
				window.location.href = '/';
			}
		}
	};

	return handleLogout;
};

export default useLogout;